// Samlad projektvy över arbetsberedningar: status, granskning, kompetensvarningar
// samt skapa, öppna, duplicera och ta bort.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { HardHat, Plus, Copy, Trash2, RefreshCw, AlertTriangle, ExternalLink, FolderInput, Settings2 } from "lucide-react";
import DocumentHeader from "@/components/BasProcess/DocumentHeader";
import { ensurePhaseFolder } from "@/lib/project/projectFolders";
import { useAuth } from "@/context/auth";
import ExternalWorkPrepDialog from "./ExternalWorkPrepDialog";
import ExternalWorkPrepPanel from "./ExternalWorkPrepPanel";
import { generateExternalWorkPrepSummary } from "@/lib/documentGenerator/externalWorkPrepSummaryGenerator";


interface Props {
  projectId: string;
}

interface Row {
  id: string;
  name: string;
  folder_id: string | null;
  review_status: string | null;
  updated_at: string | null;
  data: any;
  origin?: string | null;
  external_week?: number | null;
  external_year?: number | null;
  external_contractor?: string | null;
  external_location?: string | null;
  external_ack_at?: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  godkand: "Godkänd – klar för start",
  inskickad: "Inskickad för granskning",
  komplettering: "Kompletteras",
  utkast: "Utkast",
};


const WorkPreparationList: React.FC<Props> = ({ projectId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [originFilter, setOriginFilter] = useState<"alla" | "intern" | "extern">("alla");
  const [externalOpen, setExternalOpen] = useState(false);
  const [panelRow, setPanelRow] = useState<Row | null>(null);
  const [folderId, setFolderId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("work_preparations")
      .select(
        "id, name, folder_id, review_status, updated_at, data, origin, external_week, external_year, external_contractor, external_location, external_ack_at"
      )
      .eq("project_id", projectId)
      .is("deleted_at", null)
      .order("updated_at", { ascending: false });
    if (error) toast.error("Kunde inte läsa arbetsberedningarna");
    setRows((data ?? []) as Row[]);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const enriched = useMemo(
    () =>
      rows.map((r) => {
        const fields = (r.data?.fields ?? {}) as any;
        const comps = (fields.kompetensVerifieringar ?? []) as any[];
        const today = new Date(new Date().toDateString());
        const expired = comps.filter((k) => k.giltigTill && new Date(k.giltigTill) < today).length;
        const unverified = comps.filter(
          (k) => (k.status ?? "foreslagen") !== "verifierad" && !k.motiveringEjAktuell
        ).length;
        const isExternal = r.origin === "extern";
        return {
          ...r,
          isExternal,
          contractor: (isExternal ? r.external_contractor : fields.gjordAv) ?? "",
          week: r.external_week ? `${r.external_year ?? ""} v.${r.external_week}` : "",
          period: [fields.startdatum, fields.slutdatum].filter(Boolean).join(" – "),
          warning: isExternal
            ? ""
            : [
                expired ? `${expired} utgånget kompetensbevis` : "",
                unverified ? `${unverified} overifierade kompetenskrav` : "",
              ]
                .filter(Boolean)
                .join(" · "),
        };
      }),
    [rows]
  );

  const filtered = enriched
    .filter((r) =>
      originFilter === "alla" ? true : originFilter === "extern" ? r.isExternal : !r.isExternal
    )
    .filter((r) =>
      [r.name, r.contractor, r.week].join(" ").toLowerCase().includes(query.toLowerCase())
    )
    .sort((a, b) => {
      const aw = (a.external_year ?? 0) * 100 + (a.external_week ?? 0);
      const bw = (b.external_year ?? 0) * 100 + (b.external_week ?? 0);
      return bw - aw;
    });


  const targetFolder = async () => {
    const existing = rows.find((r) => r.folder_id)?.folder_id;
    if (existing) return existing;
    if (!user?.id) return null;
    return ensurePhaseFolder(projectId, user.id, "23", "arbetsberedning");
  };

  const createNew = async () => {
    const target = await targetFolder();
    if (!target) return toast.error("Ingen mapp för arbetsberedningar kunde skapas");
    navigate(`/arbetsberedning/${projectId}/${target}`);
  };

  const openExternalDialog = async () => {
    setFolderId(await targetFolder());
    setExternalOpen(true);
  };

  const open = (r: Row) => {
    if (r.origin === "extern") return setPanelRow(r);
    if (!r.folder_id) return toast.error("Arbetsberedningen saknar mapp");
    navigate(`/arbetsberedning/${projectId}/${r.folder_id}/${r.id}`);
  };


  const duplicate = async (r: Row) => {
    const folderId = r.folder_id ?? (await targetFolder());
    if (!folderId) return toast.error("Ingen mapp kunde hittas");
    const { error } = await supabase.from("work_preparations").insert({
      project_id: projectId,
      folder_id: folderId,
      name: `${r.name} (kopia)`,
      data: r.data,
      status: "utkast",
      review_status: "utkast",
    } as any);
    if (error) return toast.error("Kunde inte duplicera");
    toast.success("Arbetsberedningen duplicerad");
    void load();
  };

  const remove = async (r: Row) => {
    const { error } = await supabase
      .from("work_preparations")
      .update({ deleted_at: new Date().toISOString() } as any)
      .eq("id", r.id);
    if (error) return toast.error("Kunde inte ta bort");
    setRows((prev) => prev.filter((x) => x.id !== r.id));
  };

  const approved = enriched.filter((r) => r.review_status === "godkand").length;
  const externals = enriched.filter((r) => r.isExternal);

  const exportExternals = async () => {
    if (!externals.length) return toast.error("Inga externa arbetsberedningar att exportera");
    try {
      await generateExternalWorkPrepSummary(
        externals.map((r) => {
          const analysis = (r.data as any)?.externalAnalysis;
          const remarks = [
            ...(analysis?.saknas ?? []).map((s: string) => `Saknas: ${s}`),
            ...(analysis?.fortydliga ?? []).map((f: any) => `Förtydliga: ${f.punkt}`),
          ].join("; ");
          return {
            name: r.name,
            moment: (r.data as any)?.fields?.arbetsmoment ?? "",
            week: r.week,
            contractor: r.contractor,
            location: r.external_location ?? "",
            acknowledged: r.external_ack_at
              ? new Date(r.external_ack_at).toLocaleDateString("sv-SE")
              : "Nej",
            remarks,
          };
        })
      );
      toast.success("Sammanställningen exporterad");
    } catch {
      toast.error("Kunde inte exportera sammanställningen");
    }
  };


  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="23"
        title="Arbetsberedningar"
        description="Alla arbetsberedningar i projektet med granskningsstatus, period och kompetensvarningar."
        icon={HardHat}
        projectId={projectId}
        meta={
          <Badge variant="outline">
            {approved} av {enriched.length} godkända
          </Badge>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Uppdatera
            </Button>
            <Button variant="outline" size="sm" onClick={openExternalDialog}>
              <FolderInput className="mr-1 h-4 w-4" /> Registrera extern arbetsberedning
            </Button>
            {externals.length > 0 && (
              <Button variant="outline" size="sm" onClick={exportExternals}>
                Exportera externa (Word)
              </Button>
            )}

            <Button size="sm" onClick={createNew}>
              <Plus className="mr-1 h-4 w-4" /> Ny arbetsberedning
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Sök på moment, entreprenör eller vecka"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="max-w-sm"
        />
        <div className="flex items-center gap-1">
          {(["alla", "intern", "extern"] as const).map((f) => (
            <Button
              key={f}
              size="sm"
              variant={originFilter === f ? "default" : "outline"}
              onClick={() => setOriginFilter(f)}
            >
              {f === "alla" ? "Alla" : f === "intern" ? "Egna" : "Externa"}
            </Button>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="space-y-2 p-4">
          {loading && <p className="text-sm text-muted-foreground">Laddar…</p>}
          {!loading && filtered.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Inga arbetsberedningar ännu. Skapa den första med knappen ovan.
            </p>
          )}
          {filtered.map((r) => (
            <div
              key={r.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3 text-sm"
            >
              <button className="min-w-0 text-left" onClick={() => open(r)}>
                <div className="flex items-center gap-2 font-medium">
                  {r.name}
                  {r.isExternal && <Badge variant="outline">Extern</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {[r.week, r.contractor, r.period].filter(Boolean).join(" · ") ||
                    "Ingen period angiven"}
                </div>
                {r.isExternal && r.external_location && (
                  <div className="text-xs text-muted-foreground">
                    Original: {r.external_location}
                  </div>
                )}
                {r.warning && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-destructive">
                    <AlertTriangle className="h-3 w-3" /> {r.warning}
                  </div>
                )}
              </button>
              <div className="flex items-center gap-2">
                <Badge
                  className={r.review_status === "godkand" ? "bg-emerald-600 hover:bg-emerald-600" : ""}
                  variant={r.review_status === "godkand" ? "default" : "secondary"}
                >
                  {r.isExternal
                    ? r.external_ack_at
                      ? "Kvitterad av Bas-U"
                      : "Ej kvitterad"
                    : STATUS_LABELS[r.review_status ?? "utkast"] ?? "Utkast"}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {r.updated_at ? new Date(r.updated_at).toLocaleDateString("sv-SE") : ""}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => open(r)}
                  title={r.isExternal ? "Filer och AI-granskning" : "Öppna"}
                >
                  {r.isExternal ? (
                    <Settings2 className="h-4 w-4" />
                  ) : (
                    <ExternalLink className="h-4 w-4" />
                  )}
                </Button>
                {!r.isExternal && (
                  <Button variant="ghost" size="icon" onClick={() => duplicate(r)} title="Duplicera">
                    <Copy className="h-4 w-4" />
                  </Button>
                )}
                <Button variant="ghost" size="icon" onClick={() => remove(r)} title="Ta bort">
                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <ExternalWorkPrepDialog
        open={externalOpen}
        onOpenChange={setExternalOpen}
        projectId={projectId}
        folderId={folderId}
        onCreated={() => void load()}
      />

      {panelRow && (
        <ExternalWorkPrepPanel
          open={!!panelRow}
          onOpenChange={(v) => !v && setPanelRow(null)}
          projectId={projectId}
          workPreparationId={panelRow.id}
          workPreparationName={panelRow.name}
          acknowledgedAt={panelRow.external_ack_at ?? null}
          onChanged={() => void load()}
        />
      )}
    </div>
  );

};

export default WorkPreparationList;
